import { addAncillaryToOffer } from "../../../app/api/getOffers";
import { deleteAncillaryFromOffer } from "../../../app/api/deleteAncillaryFromOffer";
import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { hideLoader, showLoader } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { AncillaryStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking, IBookingResponse, ISelectedTrainLegs } from "../../../Orchestrator/Types/Index";
import {
  IAddAncillaryToOfferRequest,
  IAddonsPassengerLeg,
  IAlreadyAddonsForPassenger,
  IAlreadyAddonsLegPassengerAddons,
} from "../../../Orchestrator/Types/Interface/IAddOns";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { mergeUpgradePaymentSummary } from "../Checkout/Summary/mergeUpgradePaymentSummary";
import { syncAlreadyAddonsAction } from "./SyncAlreadyAddonsAction";
import { storeBookingResponseAtom } from "../../../Lib/Utils/tripDirectionRecovery";
import type { IPendingRefund } from "../../../Orchestrator/Recoils/AddonsAtoms";
import { buildCarriedOverAncillaryKeys, isCarriedOverAncillary } from "../../../Lib/Utils/upgradeAncillaryTwins";

type IDefaultActionResult = { status: "success" | "fail"; message?: string };

export interface IPersistAddonsOnConfirmParams {
  /**
   * Restricts the DELETE/POST phases to ancillaries on these leg IDs only.
   * `confirmAddonsPassengerListAtom` accumulates legs across multiple
   * confirm rounds (by design — the Payment page / summary read all
   * confirmed legs). Without this scope, a second-leg confirm would DELETE
   * existing ancillaries on a previously-confirmed leg because its legId
   * and categories are still in the atom.
   */
  scopeLegIds?: string[];
}

/** Write to sessionStorage only in browser contexts (SSR-safe). */
function safeSessionSet(key: string, value: string): void {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(key, value);
  }
}

/** Remove from sessionStorage only in browser contexts (SSR-safe). */
function safeSessionRemove(key: string): void {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(key);
  }
}

export class PersistAddonsOnConfirmAction extends BaseAction<
  IDefaultActionResult,
  IPersistAddonsOnConfirmParams
> {
  // Prevent double-confirm double-mutations from concurrent execute() calls.
  // Combined with `StepFlow.drainPersistentSubscriptions` (which guarantees
  // each addon-confirm handler is dispatched at most once per event) and the
  // POST idempotency guard below (which skips a candidate already matching a
  // PREBOOKED ancillary on the booking), this is the dedup layer that
  // protects against double-charging in change-date checkout.
  protected override deduplicate = true;

  /**
   * Post-persist sync of `alreadyAddonsLegPassengerAtom`. Delegates to the
   * shared `syncAlreadyAddonsAction` with `includeNonFulfilledInManageSeating: true`
   * so newly-POSTed PREBOOKED ancillaries remain visible to the next confirm
   * (diff detection in `ConfirmAddonsAction` and `OnChangeAddonForActivePassenger`
   * depends on them being present). The modal-open call in `AddOnsInitStep`
   * uses the default (FULFILLED-only) filter instead.
   */
  private async syncAlreadyAddonsFromBooking(booking: IBooking, isUpgrade: boolean) {
    syncAlreadyAddonsAction.setParams({
      booking,
      isUpgrade,
      includeNonFulfilledInManageSeating: true,
    });
    await syncAlreadyAddonsAction.execute();
    // BaseAction swallows thrown errors internally (sets status to ERROR).
    // Re-throw here so the caller's try/catch can tag the failure as the
    // non-fatal `syncAlreadyAddonsFromBooking` failure (Issue 7) rather
    // than failing the whole persist.
    const syncErr = syncAlreadyAddonsAction.status.error;
    if (syncErr) throw syncErr;
  }

  override async run(): Promise<IDefaultActionResult> {
    const patchOnConfirm =
      (getRecoilStateValue("patchAddonsOnConfirmAtom") as boolean) ?? true;
    if (!patchOnConfirm) return { status: "success" };

    const bookingResponse = getRecoilStateValue<IBookingResponse>("bookingResponseAtom");
    const bookingId = bookingResponse?.booking?.id || "";
    if (!bookingId) return { status: "fail", message: "Missing bookingId" };

    const isManageSeating = (getRecoilStateValue("isManageSeatingAtom") as boolean) || false;
    // Read isUpgrade synchronously before any await so subsequent async API calls
    // (e.g. DELETE/ADD ancillary) cannot race with a concurrent reset
    // (e.g. SearchInitStep on a new-search click) flipping the atom to false
    // mid-execution (TOCTOU guard).
    const isUpgrade = getRecoilStateValue("isUpgradeToPremiumStateAtom") as boolean;
    // Read change-date marker once at run start so the DELETE-phase category
    // bypass and the POST-phase idempotency guard agree on the same value
    // even if localStorage is mutated mid-flight.
    const isChangeDateFlow =
      typeof window !== "undefined" &&
      localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) === "true" &&
      !isManageSeating &&
      !isUpgrade;

    // Snapshot selectedTrainLegs BEFORE any await for the direction-recovery
    // signal — same race rule as ApplyCheckoutUpsellAction. The post-await
    // sibling flow that could rewrite this atom (prepareBookingSummaryAction →
    // updateLegTripSummary) is fired indirectly by addon ADD/DELETE calls;
    // reading later would key recovery off post-flip state.
    const priorSelectedTrainLegs =
      getRecoilStateValue<ISelectedTrainLegs>("selectedTrainLegsAtom");

    let confirmAddonsList =
      (getRecoilStateValue("confirmAddonsPassengerListAtom") as IAddonsPassengerLeg[]) ||
      (getRecoilStateValue("addonsPassengerListAtom") as IAddonsPassengerLeg[]) ||
      [];

    // Scope the persist operation to the legs that were just confirmed in this
    // round. Without this, confirming leg B would walk all accumulated legs in
    // confirmAddonsPassengerListAtom and fire DELETEs on leg A's ancillaries
    // (cross-leg contamination). targetLegIds + managedCategories + the POST
    // phase all derive from confirmAddonsList, so filtering here cascades.
    const scopeLegIds = this.params?.scopeLegIds;
    if (scopeLegIds && scopeLegIds.length > 0) {
      const scopeSet = new Set(scopeLegIds);
      confirmAddonsList = confirmAddonsList.filter(
        (l) => !!l?.legId && scopeSet.has(l.legId)
      );
    }

    // Snapshot addonsPassengerListAtom synchronously before any await so the
    // post-mutation isNew:false normalization operates on the atom value that was
    // current when the user confirmed — not a stale value written by a concurrent
    // action (e.g. UnmountPassengerDetailAction) after the async chain completes.
    const addonsPassengerListSnapshot =
      (getRecoilStateValue("addonsPassengerListAtom") as IAddonsPassengerLeg[]) || [];

    // Read pre-existing addons BEFORE computing targetLegIds so that
    // manage-seating "remove all" can target legs that only have existing addons
    // (i.e. when confirmAddonsList is empty but there are addons to delete).
    const alreadyAddons = (getRecoilStateValue("alreadyAddonsLegPassengerAtom") as IAlreadyAddonsLegPassengerAddons) || {};
    const allExistingAddons: IAlreadyAddonsForPassenger[] = [
      ...(alreadyAddons?.outbound || []),
      ...(alreadyAddons?.inbound || []),
    ];

    // In manage-seating, include legs with existing addons so that "remove all"
    // correctly targets those legs for deletion even when confirmAddonsList is entirely
    // empty (no leg entries at all). In upgrade flow this extension is NOT needed —
    // the leg being confirmed is always present in confirmAddonsList (even when the user
    // deselected all addons the leg entry still exists with an empty addonItem). Adding
    // all existing addon legs in upgrade would cause confirming outbound to also delete
    // unrelated inbound ancillaries. In regular checkout, targetLegIds comes only from
    // confirmAddonsList to avoid stale atom data causing spurious DELETEs.
    const targetLegIds = Array.from(
      new Set([
        ...(confirmAddonsList || []).map((l) => l?.legId).filter(Boolean),
        ...(isManageSeating
          ? allExistingAddons.map((a) => a.legId || "").filter(Boolean)
          : []),
      ])
    ) as string[];

    const mealPreferences = (getRecoilStateValue("mealPreferencesResponseAtom") as any) || {};
    const managedCategories = new Set<string>();
    for (const legId of targetLegIds) {
      Object.keys(mealPreferences?.[legId] || {}).forEach((c) => managedCategories.add(c));
    }
    for (const leg of confirmAddonsList || []) {
      for (const p of leg.addonsPassenger || []) {
        Object.keys(p?.addonItem || {}).forEach((c) => managedCategories.add(c));
      }
    }
    // In manage-seating and upgrade flows: include categories from pre-existing addons
    // so that deselected categories are still eligible for deletion — their ancillaries
    // must be removed even though the category key no longer appears in the confirmed
    // addonItem. Without this, re-confirming addons on any leg after the first confirm
    // in an upgrade flow creates duplicate PREBOOKED ancillaries. Gated on
    // isManageSeating || isUpgrade to prevent stale atom data from a prior session
    // from triggering spurious DELETEs in regular checkout.
    if (isManageSeating || isUpgrade) {
      for (const addon of allExistingAddons) {
        if (addon.category && targetLegIds.includes(addon.legId || "")) {
          managedCategories.add(addon.category);
        }
      }
    }

    const booking: IBooking | undefined = bookingResponse?.booking;

    showLoader();
    try {
      let deletedCount = 0;

      if (isManageSeating) {
        /**
         * Manage-seating only — Diff-based DELETE:
         * Only DELETE ancillary IDs the user actually removed (tracked in
         * manageAddonsRemovedAddonsAtom). Skip FULFILLED (backend silently no-ops)
         * and REFUNDED items.
         */
        const removedList =
          (getRecoilStateValue("manageAddonsRemovedAddonsAtom") as IAlreadyAddonsForPassenger[]) || [];

        const ancillariesById = new Map<string, { offerId: string; status: AncillaryStatusEnum | string; legId: string }>();
        for (const offer of booking?.bookedOffers || []) {
          for (const anc of (offer as any).ancillaries || []) {
            const id = (anc as any)?.id;
            if (id) {
              ancillariesById.set(id, {
                offerId: (offer as any).offerId,
                status: (anc as any)?.status || "",
                legId: (anc as any)?.tripCoverage?.coveredLegIds?.[0] || "",
              });
            }
          }
        }

        const deleteTargets = new Map<string, { bookedOfferId: string; ancillaryId: string }>();
        const skippedFulfilled: string[] = [];
        for (const removed of removedList) {
          const ancId = removed?.ancillaryId || "";
          if (!ancId) continue;
          const info = ancillariesById.get(ancId);
          if (!info) continue;
          if (info.status === AncillaryStatusEnum.REFUNDED) continue;
          if (info.status === AncillaryStatusEnum.FULFILLED) { skippedFulfilled.push(ancId); continue; }
          deleteTargets.set(`${info.offerId}:${ancId}`, { bookedOfferId: info.offerId, ancillaryId: ancId });
        }

        // Recompute pendingRefundFulfillmentIdsAtom every confirm so a re-
        // ticked previously-un-ticked FULFILLED item gets REMOVED from the
        // queue. The previous union-of-carryOver-and-skippedFulfilled
        // accumulated forever — the user could toggle Non Veg off, then on,
        // and it would still be sent to the backend for refund at Pay.
        //
        // Per-leg replacement: legs touched in this confirm round get their
        // queue entries replaced by the latest skippedFulfilled. Legs NOT in
        // scope (multi-leg flows where another leg has been confirmed
        // separately) keep their existing queue entries.
        if (skippedFulfilled.length > 0) {
          console.warn(
            `[PersistAddonsOnConfirmAction] Skipped ${skippedFulfilled.length} FULFILLED ancillaries in DELETE:`,
            skippedFulfilled,
          );
        }
        const existingPending = getRecoilStateValue(
          "pendingRefundFulfillmentIdsAtom",
        ) as IPendingRefund | null;
        const carryOver =
          existingPending && existingPending.bookingId === bookingId
            ? existingPending.fulfillmentIds
            : [];
        const currentLegIds = new Set(
          (confirmAddonsList || []).map((l) => l?.legId).filter((id): id is string => !!id),
        );
        const carryOverPreserved = carryOver.filter((id) => {
          const leg = ancillariesById.get(id)?.legId || "";
          // Unknown leg → keep (defensive: don't accidentally drop a
          // queued id whose ancillary we can't locate). In-scope leg → drop;
          // it'll be re-added via skippedFulfilled if still un-ticked.
          if (!leg) return true;
          return !currentLegIds.has(leg);
        });
        const newQueue = Array.from(
          new Set<string>([...carryOverPreserved, ...skippedFulfilled]),
        );
        if (newQueue.length > 0) {
          updateRecoilState("pendingRefundFulfillmentIdsAtom", {
            bookingId,
            fulfillmentIds: newQueue,
          });
        } else {
          updateRecoilState("pendingRefundFulfillmentIdsAtom", null);
        }

        if (deleteTargets.size > 0) {
          const deleteResults = await Promise.all(
            [...deleteTargets.values()].map((t) =>
              deleteAncillaryFromOffer(bookingId, t.bookedOfferId, t.ancillaryId, false).then(
                (res) => ({ ...t, res }),
              ),
            ),
          );
          const successCount = deleteResults.filter((r) => r.res.status === "ok").length;
          for (const { bookedOfferId, ancillaryId, res } of deleteResults) {
            if (res.status !== "ok") {
              console.warn(
                `[PersistAddonsOnConfirmAction] ${successCount}/${deleteResults.length} DELETEs succeeded before failure`,
              );
              throw new Error(
                `[PersistAddonsOnConfirmAction] DELETE ancillary ${ancillaryId} on offer ${bookedOfferId} failed: ${(res as any)?.message ?? "unknown error"}`,
              );
            }
          }
          deletedCount = deleteTargets.size;
          safeSessionSet("needToShowRemovedAddons", bookingId);
        }
      } else {
        /**
         * Non-manage flows (upgrade / regular checkout / change-date) — original
         * bulk replace-all delete. Scans booking ancillaries and deletes those
         * matching targetLegIds + managedCategories (pre-existing behavior,
         * restored to keep these flows untouched by the manage-seating fix).
         */
        // Change-date only: backend returns `type: "Ancillary"` (generic
        // placeholder) for newly POSTed PREBOOKED ancillaries instead of the
        // specific category ("Meals" / "Lounge" / ...). That breaks the
        // managedCategories.has(category) guard and leaves freshly-POSTed
        // ancillaries undeletable when the user deselects them in the same
        // session. Bypass the category check for PREBOOKED ancillaries on
        // targetLegIds in change-date — every PREBOOKED ancillary on the new
        // prebooked offer was POSTed by us this session, so it is a safe
        // candidate for the bulk replace-all delete.
        // (`isChangeDateFlow` is hoisted to the top of run().)

        if (booking && targetLegIds.length > 0) {
          // Upgrade-flow only: build the carry-over twin set so we never try
          // to DELETE an ancillary the backend auto-linked from the original
          // booking. Those carry status=PREBOOKED on the new upgrade offer
          // but the backend rejects deletion with
          // `TRANSFERRED_ANCILLARY_CANNOT_BE_REMOVED` (HTTP 400). Empty set
          // in non-upgrade flows is a no-op skip.
          const carriedOverKeys = isUpgrade
            ? buildCarriedOverAncillaryKeys(booking)
            : new Set<string>();

          const deleteTargets = new Map<string, { bookedOfferId: string; ancillaryId: string }>();
          for (const offer of booking.bookedOffers || []) {
            for (const ancillary of offer.ancillaries || []) {
              const ancillaryId = (ancillary as any)?.id || "";
              const status = (ancillary as any)?.status || "";
              const legId = (ancillary as any)?.tripCoverage?.coveredLegIds?.[0] || "";
              let category = (ancillary as any)?.type || "";
              if (!category) {
                const productId = (ancillary as any)?.products?.[0]?.productId;
                const productDetails = productId
                  ? (offer.products || []).find((p: any) => p.id === productId)
                  : undefined;
                category = productDetails?.description || "";
              }

              if (!ancillaryId || !legId) continue;
              if (status === AncillaryStatusEnum.REFUNDED) continue;
              if (!targetLegIds.includes(legId)) continue;

              // Skip backend-auto-linked twins on the upgrade offer — backend
              // rejects DELETE on these as TRANSFERRED_ANCILLARY_CANNOT_BE_REMOVED.
              if (
                isUpgrade &&
                status === AncillaryStatusEnum.PREBOOKED &&
                isCarriedOverAncillary(ancillary as any, carriedOverKeys)
              ) {
                continue;
              }

              const skipCategoryGuard =
                isChangeDateFlow && status === AncillaryStatusEnum.PREBOOKED;
              if (!skipCategoryGuard) {
                if (managedCategories.size === 0 || !managedCategories.has(category)) continue;
              }

              deleteTargets.set(`${offer.offerId}:${ancillaryId}`, {
                bookedOfferId: offer.offerId,
                ancillaryId,
              });
            }
          }

          const deleteResults = await Promise.all(
            [...deleteTargets.values()].map((t) =>
              deleteAncillaryFromOffer(bookingId, t.bookedOfferId, t.ancillaryId, false).then(
                (res) => ({ ...t, res })
              )
            )
          );

          const successCount = deleteResults.filter((r) => r.res.status === "ok").length;
          for (const { bookedOfferId, ancillaryId, res } of deleteResults) {
            if (res.status !== "ok") {
              console.warn(
                `[PersistAddonsOnConfirmAction] ${successCount}/${deleteResults.length} DELETEs succeeded before failure`
              );
              throw new Error(
                `[PersistAddonsOnConfirmAction] DELETE ancillary ${ancillaryId} on offer ${bookedOfferId} failed: ${(res as any)?.message ?? "unknown error"}`
              );
            }
          }

          deletedCount = deleteTargets.size;

          if (deletedCount > 0) {
            safeSessionRemove("needToShowRemovedAddons");
          }
        }
      }

      // Early-return for "remove all addons" case — DELETEs already ran above.
      // We still need to refresh the booking and payment summary so the UI
      // reflects the removed addons (stale summary bug: Issue 1).
      if ((confirmAddonsList || []).length === 0) {
        if (deletedCount > 0) {
          const refreshed = await getBooking(bookingId, false);
          if (refreshed.status === "ok" && refreshed.answer) {
            // Gateway flips IN_BOUND→OUT_BOUND on post-/replace bookings;
            // recover before any consumer reads booking.trips from the atom.
            // `priorSelectedTrainLegs` was snapshotted at run() start (above)
            // before any await, so it reflects pre-flip state.
            const recoveredResponse = storeBookingResponseAtom(refreshed.answer, priorSelectedTrainLegs) ?? refreshed.answer;
            const bookingForSummary = recoveredResponse.booking;
            // Check isManageSeating first: if both flags are true (e.g. stale
            // isUpgrade from a prior upgrade that preceded this manage-seating
            // session), the manage-seating branch is the correct summary path.
            if (isManageSeating) {
              prepareBookingSummaryAction.setParams({
                ...bookingForSummary,
                isManageSeating: true,
              } as any);
              await prepareBookingSummaryAction.execute();
            } else if (isUpgrade) {
              mergeUpgradePaymentSummary(bookingForSummary);
            } else {
              prepareBookingSummaryAction.setParams({
                ...bookingForSummary,
                isManageAddons: true,
              } as any);
              await prepareBookingSummaryAction.execute();
            }
            try {
              await this.syncAlreadyAddonsFromBooking(bookingForSummary, isUpgrade && !isManageSeating);
            } catch (syncError) {
              console.warn(
                "[PersistAddonsOnConfirmAction] syncAlreadyAddonsFromBooking failed (non-fatal):",
                syncError
              );
            }
          }
        }
        updateRecoilState("addonFlowStartedAtom", false);
        return { status: "success" };
      }

      /**
       * Add ancillary (Addons) to booked offer
       * POST /bookings/{bookingId}/booked-offers/{offerId}/ancillaries
       */
      if ((confirmAddonsList || []).length > 0) {
        const allTripLegPassengerAddons: IAddAncillaryToOfferRequest[] = [];
        // Track each candidate's product code so the change-date idempotency
        // guard below can match against the booking's PREBOOKED ancillaries
        // (which expose `products[0].productId` rather than ancillaryOfferId).
        // Aligned 1:1 with allTripLegPassengerAddons by index.
        const productCodes: string[] = [];

        for (const addonsDetail of confirmAddonsList) {
          for (const addonsPass of addonsDetail.addonsPassenger || []) {
            for (const addonsItem of Object.values(addonsPass?.addonItem || {}) as any[]) {
              if (!addonsItem?.bookedOfferId || !addonsItem?.ancillaryOfferId) continue;
              // Manage-seating only: skip items the user didn't newly add
              // (isNew !== true). Other flows keep the pre-change behavior:
              // POST every item in confirmAddonsList, relying on the bulk
              // replace-all delete above to have cleared the slate first.
              if (isManageSeating && addonsItem?.isNew !== true) continue;
              allTripLegPassengerAddons.push({
                offerId: addonsItem?.additionalOfferId || "",
                bookedOfferId: addonsItem?.bookedOfferId || "",
                ancillaryOfferId: addonsItem?.ancillaryOfferId || "",
                passengerRefs: [addonsPass.passengerRef],
                tripCoverage: { coveredTripId: addonsItem?.tripCoverageId || "" },
              });
              productCodes.push(String(addonsItem?.type || ""));
            }
          }
        }

        // Change-date idempotency: if a duplicate AddOns.Data.Confirm dispatch
        // (e.g. accumulated event-bus subscriptions from FlowRunner re-running
        // onEventSetup without teardown) sneaks past the AddOnsInitStep guard,
        // a second POST would create a duplicate PREBOOKED ancillary on the
        // same offer/passenger/trip. Skip any candidate whose
        // (bookedOfferId, productCode, passengerRef, coveredTripId) already
        // matches a PREBOOKED ancillary on the live booking. Scoped to
        // change-date to keep regular checkout / upgrade / manage-seating
        // semantics unchanged.
        if (isChangeDateFlow && booking) {
          const existingSignatures = new Set<string>();
          for (const offer of booking.bookedOffers || []) {
            const productIdToCode = new Map<string, string>();
            for (const p of (offer.products || []) as any[]) {
              if (p?.id && p?.code) productIdToCode.set(p.id, String(p.code));
            }
            for (const anc of (offer as any).ancillaries || []) {
              if (anc?.status !== AncillaryStatusEnum.PREBOOKED) continue;
              const productId = (anc as any)?.products?.[0]?.productId || "";
              const code = productIdToCode.get(productId) || "";
              const tripId = (anc as any)?.tripCoverage?.coveredTripId || "";
              for (const pid of (anc as any)?.passengerIds || []) {
                existingSignatures.add(
                  `${(offer as any).offerId}|${code}|${pid}|${tripId}`,
                );
              }
            }
          }

          const filteredAddons: IAddAncillaryToOfferRequest[] = [];
          for (let i = 0; i < allTripLegPassengerAddons.length; i++) {
            const item = allTripLegPassengerAddons[i];
            const code = productCodes[i] || "";
            const passengerRef = item.passengerRefs[0] || "";
            const sig = `${item.bookedOfferId}|${code}|${passengerRef}|${item?.tripCoverage?.coveredTripId}`;
            if (code && existingSignatures.has(sig)) continue;
            filteredAddons.push(item);
          }
          allTripLegPassengerAddons.length = 0;
          allTripLegPassengerAddons.push(...filteredAddons);
        }

        if (allTripLegPassengerAddons.length > 0) {
          // Merge passenger refs for identical (bookedOfferId, offerId, ancillaryOfferId, tripId) tuples.
          // bookedOfferId is included in the key so cross-offer passengers are never conflated.
          const mergeOffers = allTripLegPassengerAddons.reduce((acc: any, item) => {
            const key = `${item.bookedOfferId}-${item.offerId}-${item.ancillaryOfferId}-${item?.tripCoverage?.coveredTripId}`;
            acc[key] = acc[key] || { ...item, passengerRefs: [] };
            acc[key].passengerRefs.push(...item.passengerRefs);
            return acc;
          }, {});

          // Deduplicate passengerRefs so a multi-leg trip where confirmAddonsList
          // has one entry per leg for the same ancillary+trip doesn't send the
          // same passenger ref twice in one POST (which would create duplicate ancillaries).
          const mergedRequests = (Object.values(mergeOffers) as IAddAncillaryToOfferRequest[]).map(
            (req) => ({ ...req, passengerRefs: [...new Set(req.passengerRefs)] })
          );

          const requestsByBookedOfferId = mergedRequests.reduce((acc: any, item) => {
            const key = `${item?.bookedOfferId || ""}`;
            acc[key] = [...(acc[key] || []), item];
            return acc;
          }, {});

          const bookedOfferIds = Object.keys(requestsByBookedOfferId).filter(Boolean);

          // Parallelise by bookedOfferId; within each offer, POST requests are sequential
          // to respect per-offer ordering guarantees from the backend.
          for (const bookedOfferId of bookedOfferIds) {
            const offerAddons: IAddAncillaryToOfferRequest[] =
              requestsByBookedOfferId[bookedOfferId] || [];
            for (const req of offerAddons) {
              const { bookedOfferId: _ignore, ...body } = req as any;
              const res = await addAncillaryToOffer(body, bookingId, bookedOfferId, false);
              if (res.status !== "ok") {
                throw new Error(
                  `[PersistAddonsOnConfirmAction] POST ancillary to offer ${bookedOfferId} failed: ${(res as any)?.message ?? "unknown error"}`
                );
              }
            }
          }

          if (bookedOfferIds.length > 0) {
            if (isManageSeating) {
              safeSessionSet("needToShowAddedAddons", bookingId);
            } else {
              safeSessionRemove("needToShowAddedAddons");
            }
          }
        }
      }

      const updatedBookingResponse = await getBooking(bookingId, false);
      if (updatedBookingResponse.status === "ok" && updatedBookingResponse.answer) {
        // Gateway flips IN_BOUND→OUT_BOUND on post-/replace bookings;
        // recover before any consumer reads booking.trips from the atom.
        // `priorSelectedTrainLegs` snapshotted at run() start (pre-await).
        const recoveredResponse = storeBookingResponseAtom(updatedBookingResponse.answer, priorSelectedTrainLegs) ?? updatedBookingResponse.answer;

        const bookingForSummary = recoveredResponse.booking;

        // Check isManageSeating first: if both flags are true (e.g. stale
        // isUpgrade from a prior upgrade that preceded this manage-seating
        // session), the manage-seating branch is the correct summary path.
        if (isManageSeating) {
          prepareBookingSummaryAction.setParams({
            ...bookingForSummary,
            isManageSeating: true,
          } as any);
          await prepareBookingSummaryAction.execute();
        } else if (isUpgrade) {
          // Patch addon/seat data directly into the existing upgrade summary,
          // preserving fare delta, exchange fees, and alreadyPaid intact.
          mergeUpgradePaymentSummary(bookingForSummary);
        } else {
          prepareBookingSummaryAction.setParams({
            ...bookingForSummary,
            isManageAddons: true,
          } as any);
          await prepareBookingSummaryAction.execute();
        }

        // syncAlreadyAddonsFromBooking is non-fatal — a failure here must not
        // surface as a booking-persist error since the mutations already succeeded.
        try {
          await this.syncAlreadyAddonsFromBooking(bookingForSummary, isUpgrade && !isManageSeating);
        } catch (syncError) {
          console.warn(
            "[PersistAddonsOnConfirmAction] syncAlreadyAddonsFromBooking failed (non-fatal):",
            syncError
          );
        }
      }

      // Helper: set isNew:false on every addonItem inside a legs list. Shared
      // by the two atoms we need to normalize after a successful persist.
      const normalizeLegs = (legs: IAddonsPassengerLeg[]) =>
        legs.map((leg) => ({
          ...leg,
          addonsPassenger: (leg.addonsPassenger || []).map((p: any) => ({
            ...p,
            addonItem: Object.fromEntries(
              Object.entries(p?.addonItem || {}).map(([k, v]: any) => [
                k,
                v && typeof v === "object" ? { ...v, isNew: false } : v,
              ])
            ),
          })),
        }));

      if (addonsPassengerListSnapshot.length > 0) {
        updateRecoilState(
          "addonsPassengerListAtom",
          normalizeLegs(addonsPassengerListSnapshot)
        );
      }

      // Also normalize confirmAddonsPassengerListAtom so the next modal open
      // via SetExistingAddonsAction (which copies from confirm -> addons list)
      // cannot resurrect isNew:true on items that were already POSTed. Without
      // this, the POST filter `isManageSeating && !isNew` lets previously-
      // persisted items through on the next confirm and the server receives
      // duplicate ancillaries.
      const confirmAddonsSnapshot =
        (getRecoilStateValue("confirmAddonsPassengerListAtom") as IAddonsPassengerLeg[]) || [];
      if (confirmAddonsSnapshot.length > 0) {
        updateRecoilState(
          "confirmAddonsPassengerListAtom",
          normalizeLegs(confirmAddonsSnapshot)
        );
      }

      updateRecoilState("manageAddonsNewConfirmedAddonsAtom", []);
      updateRecoilState("manageAddonsRemovedAddonsAtom", []);
      updateRecoilState("addonFlowStartedAtom", false);

      return { status: "success" };
    } catch (e: any) {
      console.error("[PersistAddonsOnConfirmAction] Failed to persist add-ons:", e?.message);

      // Payment summary rollback: fetch the actual committed booking state and
      // rebuild the summary so that failed ancillary POSTs/DELETEs are never
      // reflected in the displayed price. Runs in its own try/catch so that a
      // secondary network failure here never masks the primary error.
      try {
        const rollbackBooking = await getBooking(bookingId, false);
        if (rollbackBooking.status === "ok" && rollbackBooking.answer) {
          // Recover directions AND write back to bookingResponseAtom — every
          // consumer that subscribes to the atom (CheckoutTripSummary,
          // MultiBookingSeatingSummary, every booking.trips[].direction reader)
          // needs the corrected state too, not just the local summary rebuild.
          // priorSelectedTrainLegs was snapshotted at run() start (pre-await).
          const recoveredResponse = storeBookingResponseAtom(rollbackBooking.answer, priorSelectedTrainLegs) ?? rollbackBooking.answer;
          const bookingForRollback = recoveredResponse.booking;
          if (isUpgrade) {
            mergeUpgradePaymentSummary(bookingForRollback);
          } else {
            prepareBookingSummaryAction.setParams({
              ...bookingForRollback,
              ...(isManageSeating ? { isManageSeating: true } : { isManageAddons: true }),
            } as any);
            await prepareBookingSummaryAction.execute();
          }
        }
      } catch (rollbackError: any) {
        console.warn(
          "[PersistAddonsOnConfirmAction] Payment summary rollback failed (non-fatal):",
          rollbackError?.message,
        );
      }

      updateRecoilState("addonFlowStartedAtom", false);
      return { status: "fail", message: e?.message || "Failed to persist add-ons" };
    } finally {
      hideLoader();
    }
  }
}

export const persistAddonsOnConfirmAction = new PersistAddonsOnConfirmAction();
